/**
 * CertificateManagementView
 * Admin page for digital certificate management
 */

import React, { useCallback, useState } from 'react';
import { Container, Row, Col, Card, Button } from 'react-bootstrap';

import { useSpdCore } from '../../../common/hooks/useSpdCore';
import { useNotifications } from '../../../common/hooks/useNotifications';
import { PasswordInputModal } from '../../components/Certificates/PasswordInputModal';
import { CertificateEditor } from '../../components/Certificates/CertificateEditor';
import { CertificateGrid } from '../../components/Certificates/CertificateGrid';
import { CaDownloadButton } from '../../components/Certificates/CaDownloadButton';
import { CertificateResponseDto } from '../../../common/spdCore/autogenerated/spdApiClient';

export const CertificateManagementView: React.FC = () => {
  const spd = useSpdCore();
  const notifications = useNotifications();

  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showCertificateEditor, setShowCertificateEditor] = useState(false);
  const [pendingCert, setPendingCert] = useState<CertificateResponseDto | null>(
    null,
  );
  const [refresh, setRefresh] = useState(false);

  const handleGenerateNew = () => {
    setShowCertificateEditor(true);
  };

  const handlePasswordConfirm = async (password: string) => {
    if (!pendingCert?.id) {
      return;
    }

    try {
      setShowPasswordModal(false);

      await spd.certificates.downloadCertificateWithKey(
        pendingCert.id,
        password,
        pendingCert.commonName,
      );

      notifications.onPromise(Promise.resolve());
    } catch (error) {
      notifications.onPromise(Promise.reject(error));
    } finally {
      setPendingCert(null);
    }
  };

  const handleDownloadCertificate = async (cert: CertificateResponseDto) => {
    if (!cert.id) {
      return;
    }

    setShowPasswordModal(true);
    setPendingCert(cert);
  };

  const getData = useCallback(
    async (
      pageSize: number,
      pageNumber: number,
      orderBy?: string,
      orderDirection?: string,
      filterText?: string,
    ) => {
      return await spd.certificates.getAllByPortion(
        pageSize,
        pageNumber,
        orderBy,
        orderDirection,
        filterText,
        handleDownloadCertificate,
      );
    },
    [spd.certificates],
  );

  return (
    <Container className="py-4">
      <Row className="mb-4">
        <Col>
          <h1>Zarządzanie certyfikatami drukarek</h1>
        </Col>
      </Row>

      <Row className="mb-4">
        <Col lg={8}>
          <Card>
            <Card.Header>
              <Card.Title className="mb-0">Akcje</Card.Title>
            </Card.Header>
            <Card.Body>
              <div className="d-flex gap-2 flex-wrap">
                <CaDownloadButton />
                <Button variant="success" onClick={handleGenerateNew}>
                  Wygeneruj nowy certyfikat
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="mb-4">
        <Col lg={12}>
          <Card>
            <Card.Header>
              <Card.Title className="mb-0">Lista certyfikatów</Card.Title>
            </Card.Header>
            <Card.Body>
              <CertificateGrid
                getData={getData}
                getDataCount={spd.certificates.getAllCount}
                refresh={refresh}
              />
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <PasswordInputModal
        show={showPasswordModal}
        onHide={() => {
          setShowPasswordModal(false);
          setPendingCert(null);
        }}
        onConfirm={handlePasswordConfirm}
      />

      <CertificateEditor
        show={showCertificateEditor}
        onHide={() => setShowCertificateEditor(false)}
        onSuccess={() => {
          setRefresh(prev => !prev);
        }}
      />
    </Container>
  );
};
